import { useQuery } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LikeOverlay from '../../components/like/LikeOverlay';
import PriceTimeBadge from '../../components/price-viewer/PriceTimeBadge';
import { generalHelpers, uiHelpers } from '../../helpers';
import { holdPlace } from '../../helpers/general.helper';
import { useGeneralErrorHandler } from '../../hooks/useGeneralErrorHandler';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useMembership } from '../../hooks/users';
import { quickPriceChangeSpan } from '../../modules/Insight/StockPage';
import { LatestQuote } from '../../py-generated/datalouder/price/types';
import { dailypriceService } from '../../services';

const INDEX_LIST = [
  'HK:^HSI',
  'HK:^HSTECH',
  'US:^DJI',
  'US:^IXIC',
  'US:^GSPC',
  'CRPT:BTCUSDT',
];

const amountColor = index =>
  (index.c - index.p_c) / index.p_c > 0 ? 'green' : 'red';

const IndexBar: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const nagivate = useNavigate();
  const hasMembership = useMembership();
  const handleClick = useCallback(
    async (stockCode: string) => {
      stockCode =
        stockCode === 'BTC'
          ? 'btcusdt'
          : stockCode.replace('^', '').toLowerCase();
      nagivate(`/lookup/${stockCode}`);
    },
    [nagivate]
  );

  const { data: indexPrices, error } = useQuery(
    ['realtimePrice', ...INDEX_LIST],
    () => dailypriceService.realtimePrice(INDEX_LIST),
    { refetchInterval: 120_000 }
  );
  useGeneralErrorHandler(error);

  return (
    <Row md={3} style={{ justifyContent: isMobile ? 'none' : 'center' }}>
      {(indexPrices || Array.from({ length: INDEX_LIST.length })).map(
        (index: LatestQuote, i) => (
          <Col
            key={i}
            className="index-column"
            onClick={
              index ? () => handleClick(index.ticker) : generalHelpers.noop
            }
          >
            {holdPlace(
              indexPrices !== undefined,
              () => (
                <div>
                  <LikeOverlay stockCode={index.ticker} placement="bottom">
                    <span>
                      {t(index.ticker.replace('^', '').toLowerCase())}
                    </span>
                  </LikeOverlay>
                  {holdPlace(indexPrices !== undefined, () => (
                    <PriceTimeBadge ts={index.ts} />
                  ))}
                </div>
              ),
              24
            )}
            <div className="index-price">
              <div
                className="index-price-part"
                style={{
                  color: index ? amountColor(index) : 'black',
                  fontWeight: 'bold',
                }}
              >
                {holdPlace(indexPrices !== undefined, () =>
                  index?.ticker !== '^IXIC' || hasMembership
                    ? uiHelpers.formatMoney(index.c)
                    : ''
                )}
              </div>
              <div className="index-price-part">
                {holdPlace(indexPrices !== undefined, () =>
                  index?.ticker !== '^IXIC' || hasMembership
                    ? quickPriceChangeSpan(index.c, index.p_c)
                    : t('click_to_view_detail')
                )}
              </div>
            </div>
          </Col>
        )
      )}
    </Row>
  );
};

export default IndexBar;
